import React, { useState, useMemo } from 'react';

import PropTypes from 'prop-types';

import { useCurrentProjectId, useUser } from '@adsk/alloy-provider';
import { Box, Flex } from '@adsk/alloy-react-box';
import Button, { LinkButton } from '@adsk/alloy-react-button';
import Checkbox from '@adsk/alloy-react-checkbox';
import { displayDate, VALID_DATE_FORMATS } from '@adsk/alloy-react-datetime';
import EmptyState from '@adsk/alloy-react-empty-state';
import FormField from '@adsk/alloy-react-form-field';
import { useLocalState } from '@adsk/alloy-react-helpers';
import HighlightText from '@adsk/alloy-react-highlight-text';
import { XIcon, MagnifyingGlassIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import Panel from '@adsk/alloy-react-panel';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import { RadioGroup, RadioButton } from '@adsk/alloy-react-radio-button';
import theme from '@adsk/alloy-react-theme';
import Tooltip, { OverflowTooltip } from '@adsk/alloy-react-tooltip';
import { VirtualizedSelectBox } from '@adsk/alloy-select-box';

import AccordionSelectBox from './components/AccordionSelectBox';
import SearchBox from './components/SearchBox';
import StatusSelectBox from './components/StatusSelectBox';
import { SEARCH_MODE, SHEET_UNTAGGED } from './helpers/consts';
import {
  filterSuggestions,
  updateSuggestions,
  getGroupedVersionSets,
  partition,
  isDefaultCollection,
} from './helpers/utils';
import { useCollection } from './providers/CollectionProvider';
import { useSearchPanel } from './providers/SearchPanelProvider';
import { useSheetFilter } from './providers/SheetFilterProvider';

const SheetSearchPanel = ({ filters, setFilters, open }) => {
  const { T, locale } = useLocale(({ locale }) =>
    require(`./i18n/${locale}.json`),
  );
  const {
    isEnabled: isCollectionEnabled,
    collections,
    selectedId: currentCollectionId,
    isLoading,
    hasError,
    retry,
  } = useCollection();
  const {
    allVersionSets: allVersionSetsList,
    tags,
    allVersionSetsLoading,
    allVersionSetsError,
    tagsLoading,
    tagsError,
    retryTags,
    retryAllVersionSets,
  } = useSheetFilter();
  const {
    inputText,
    setId: panelSetId,
    tags: panelTags,
    withAllTags: panelWithAllTags,
    searchMode,
    setInputText,
    setSetId: setPanelSetId,
    setTags: setPanelTags,
    setWithAllTags: setPanelWithAllTags,
    setSearchMode,
    setCachedSettings,
  } = useSearchPanel();

  const {
    searchExpression,
    setId: filterSetId,
    tags: filterTags,
  } = filters || {};
  const underSearchMode =
    !!searchExpression ||
    !!filterSetId ||
    (!!filterTags && filterTags.length > 0);

  const [showSearchBoxMenu, setShowSearchBoxMenu] = useState(false);

  const projectId = useCurrentProjectId();
  const { user } = useUser();
  const SEARCH_SUGGESTION_CACHE_KEY = `sheetsPickerSearchSuggestions_${projectId}_${user?.id}`;
  const [cachedSuggestions, setCachedSuggestions] = useLocalState(
    SEARCH_SUGGESTION_CACHE_KEY,
    [],
  );

  const [defaultCollections, userCollections] = useMemo(
    () => partition(collections, isDefaultCollection),
    [collections],
  );
  const collectionList = useMemo(
    () => [...defaultCollections, ...userCollections],
    [defaultCollections, userCollections],
  );
  const hasUserCollections = userCollections.length > 0;
  const enableCollectionSearch = isCollectionEnabled && hasUserCollections;

  const searchAll = enableCollectionSearch && searchMode === SEARCH_MODE.ALL;

  const searchInputRef = React.useRef(null);
  const searchModeRef = React.useRef(underSearchMode);

  const suggestions = useMemo(
    () => filterSuggestions(cachedSuggestions, inputText),
    [cachedSuggestions, inputText],
  );

  const defaultGroupName = useMemo(() => T.translate('UNGROUPED'), [T]);

  const allVersionSets = useMemo(
    () =>
      allVersionSetsList.map((set) => ({
        ...set,
        subtext: T.translate('VERSION_SET_DATE', {
          issuanceDate: set.issuanceDate
            ? displayDate(new Date(set.issuanceDate), {
                format: VALID_DATE_FORMATS.DEFAULT,
                locale,
              })
            : '',
        }),
      })),
    [allVersionSetsList, T, locale],
  );

  const groupedVersionSets = useMemo(
    () =>
      getGroupedVersionSets(allVersionSets, defaultGroupName, collectionList),
    [allVersionSets, defaultGroupName, collectionList],
  );

  const versionSets = useMemo(() => {
    if (!enableCollectionSearch) return allVersionSets;
    if (searchAll) return groupedVersionSets;
    if (!currentCollectionId) return [];
    return (
      groupedVersionSets.find(({ key }) => key === currentCollectionId)
        ?.options || []
    );
  }, [
    enableCollectionSearch,
    allVersionSets,
    searchAll,
    groupedVersionSets,
    currentCollectionId,
  ]);

  const untaggedLabel = useMemo(() => T.translate('SHEET_UNTAGGED'), [T]);

  const tagOptions = useMemo(
    () => [
      { value: SHEET_UNTAGGED, label: untaggedLabel },
      ...tags.map((tag) => ({ value: tag, label: tag })),
    ],
    [tags, untaggedLabel],
  );

  const selectedSet = useMemo(
    () =>
      panelSetId
        ? (searchAll ? allVersionSets : versionSets).find(
            ({ id }) => id === panelSetId,
          )
        : null,
    [panelSetId, searchAll, allVersionSets, versionSets],
  );

  const selectedTags = useMemo(
    () =>
      panelTags && panelTags.length > 0
        ? tagOptions.filter(({ value }) => panelTags.includes(value))
        : [],
    [panelTags, tagOptions],
  );

  React.useEffect(() => {
    searchModeRef.current = underSearchMode;
  }, [underSearchMode]);

  React.useEffect(() => {
    if (open) {
      setShowSearchBoxMenu(false);
      if (searchModeRef.current === false) {
        // Add gap to wait animation transition of drawer
        window.setTimeout(
          () => searchInputRef.current && searchInputRef.current.focus(),
          300,
        );
      }
    }
  }, [open]);

  React.useEffect(() => {
    if (panelSetId && !selectedSet) setPanelSetId(null);
  }, [panelSetId, selectedSet, setPanelSetId]);

  const trimFilters = (filters) => {
    Object.keys(filters).forEach((key) => {
      const filter = filters[key];
      if (!filter || (Array.isArray(filter) && filter.length < 1)) {
        delete filters[key];
      }
    });
    return filters;
  };

  const panelFilters = trimFilters({ setId: panelSetId, tags: panelTags });

  const handleClearPanelFilters = () => {
    setPanelSetId(null);
    setPanelTags([]);
    setPanelWithAllTags(false);
  };

  const isInvalidSearchInput = () =>
    inputText ? inputText.length > 255 : Object.keys(panelFilters).length === 0;

  const handleSearchInputChange = (value) => {
    setShowSearchBoxMenu(true);
    setInputText(value);
  };

  const handleSearchModeChange = (value) => {
    setSearchMode(value);
    setCachedSettings({ searchMode: value });
  };

  const handleSearch = () => {
    if (isInvalidSearchInput()) return;

    setFilters(
      trimFilters({
        searchExpression: inputText,
        setId: panelSetId,
        tags: panelTags,
        withAllTags: panelWithAllTags,
        searchAll,
      }),
    );

    if (inputText)
      setCachedSuggestions(updateSuggestions(cachedSuggestions, inputText));
  };

  const valueTagRenderer = (item) => {
    return (
      <Flex
        flexDirection="row"
        justifyContent="center"
        alignItems="center"
        backgroundColor="charcoal100"
        padding="4px 8px"
        margin="2px"
        style={{
          '&:hover': {
            background: theme.colors.charcoal200,
            cursor: 'default',
          },
        }}
      >
        <div style={{ maxWidth: 160 }}>
          <OverflowTooltip
            style={{
              maxWidth: 300,
              wordBreak: 'normal',
              wordWrap: 'break-word',
            }}
            autoTipOnEllipsis
          >
            {item.label}
          </OverflowTooltip>
        </div>
        <LinkButton
          style={{
            '&:focus': {
              'box-shadow': 'none',
            },
            marginLeft: 4,
            color: theme.colors.charcoal600,
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setPanelTags(panelTags.filter((tag) => tag !== item.value));
          }}
        >
          <XIcon size={16} />
        </LinkButton>
      </Flex>
    );
  };

  const areaHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    ...theme.typography.heading5,
    marginBottom: '16px',
  };

  const renderSearchSettings = () => {
    if (!enableCollectionSearch) return null;
    const currentCollection = collections.find(
      ({ id }) => id === currentCollectionId,
    );
    return (
      <div style={{ marginTop: '16px', marginBottom: '22px' }}>
        <div style={areaHeaderStyle}>
          {T.translate('SEARCH_AND_FILTER_SETTINGS')}
        </div>
        <RadioGroup
          value={searchMode}
          variant="vertical"
          onChange={handleSearchModeChange}
        >
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <RadioButton value={SEARCH_MODE.CURRENT} />
            <span style={{ marginLeft: '12px' }}>
              {T.translate(
                isDefaultCollection(currentCollection)
                  ? 'UNGROUPED_SHEETS'
                  : 'CURRENT_COLLECTION',
              )}
            </span>
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              marginTop: '8px',
            }}
          >
            <RadioButton value={SEARCH_MODE.ALL} />
            <span style={{ marginLeft: '12px' }}>
              {T.translate('ALL_SHEETS')}
            </span>
          </label>
        </RadioGroup>
      </div>
    );
  };

  const renderFooter = () => {
    const searchButton = (
      <Button
        style={{ width: '100%' }}
        variant={Button.VARIANTS.PRIMARY}
        renderIcon={(p) => <MagnifyingGlassIcon {...p} />}
        disabled={isInvalidSearchInput()}
        onClick={handleSearch}
      >
        {T.translate('SEARCH')}
      </Button>
    );
    return (
      <div style={{ padding: '10px 16px' }}>
        {inputText && inputText.length > 255 ? (
          <Tooltip
            content={T.translate(
              inputText.length < 2 ? 'MIN_INPUT_LENGTH' : 'MAX_INPUT_LENGTH',
            )}
            placement={Tooltip.PLACEMENTS.TOP_END}
          >
            {searchButton}
          </Tooltip>
        ) : (
          searchButton
        )}
      </div>
    );
  };

  const optionRenderer = (
    option,
    _,
    {
      labelKey,
      labelGetter,
      subtextKey,
      subtextGetter,
      stateAndHelpers: { inputValue },
    },
    disabled,
  ) => {
    const label = labelGetter ? labelGetter(option) : option[labelKey];
    const subtext = subtextGetter ? subtextGetter(option) : option[subtextKey];
    return (
      <Box
        title={label}
        typography="ellipsis"
        color={disabled ? 'charcoal500' : 'charcoal900'}
      >
        <HighlightText matched={inputValue} text={label} />
        {!!subtext && (
          <Box
            title={subtext}
            typography="bodyMedium"
            color={!disabled && 'charcoal700'}
            style={theme.typography.ellipsis}
          >
            <HighlightText matched={inputValue} text={subtext} />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Panel
      open={open}
      width={Panel.WIDTHS.SMALL}
      renderHeader={() => null}
      renderFooter={renderFooter}
    >
      <Panel.Body style={{ overflow: 'auto', fontSize: '12px' }}>
        {isLoading ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <ProgressRing size={ProgressRing.SIZES.MEDIUM} />
          </div>
        ) : hasError ? (
          <EmptyState
            style={{ height: '100%', padding: '0px' }}
            hideIllustration
            title={T.translate('SOMETHING_WENT_WRONG')}
          >
            <LinkButton onClick={retry}>{T.translate('TRY_AGAIN')}</LinkButton>
          </EmptyState>
        ) : (
          <>
            <SearchBox
              ref={searchInputRef}
              style={{
                marginBottom: '16px',
                'input:disabled': {
                  background: theme.colors.white,
                },
              }}
              placeholder={T.translate('SEARCH')}
              value={inputText || ''}
              suggestions={showSearchBoxMenu ? suggestions : []}
              onClick={() => setShowSearchBoxMenu(true)}
              onChange={handleSearchInputChange}
              onSearch={handleSearch}
              onSelect={(value) => setInputText(value)}
            />
            {renderSearchSettings()}
            <div
              style={{
                borderTop: `1px solid ${theme.colors.charcoal100}`,
                marginTop: '22px',
                paddingTop: '16px',
              }}
            >
              <div style={areaHeaderStyle}>
                {Object.keys(panelFilters).length > 0 ? (
                  <>
                    {T.translate('FILTER_TITLE_WITH_COUNT', {
                      count: Object.keys(panelFilters).length,
                    })}
                    <LinkButton
                      style={{
                        marginLeft: '8px',
                        fontSize: '12px',
                        lineHeight: '16px',
                      }}
                      onClick={handleClearPanelFilters}
                    >
                      {T.translate('CLEAR')}
                    </LinkButton>
                  </>
                ) : (
                  T.translate('FILTER_TITLE')
                )}
              </div>
              <FormField
                label={T.translate('VERSION_SET')}
                labelVariant={FormField.LABEL_VARIANTS.TOP}
                style={{ marginBottom: 16 }}
              >
                <StatusSelectBox
                  searchable
                  clearable
                  valueKey="id"
                  labelKey="name"
                  value={selectedSet || null}
                  isLoading={allVersionSetsLoading}
                  loadError={allVersionSetsError}
                  loadErrorContent={T.translate('LOAD_FILTER_ERROR')}
                  onRetry={retryAllVersionSets}
                  placeholder={T.translate('SELECT')}
                  optionRenderer={optionRenderer}
                  onChange={(option) =>
                    setPanelSetId(option ? option.id : null)
                  }
                  {...(searchAll
                    ? { groupedOptions: versionSets }
                    : { options: versionSets })}
                >
                  {(props) =>
                    searchAll ? (
                      <AccordionSelectBox {...props} />
                    ) : (
                      <VirtualizedSelectBox {...props} />
                    )
                  }
                </StatusSelectBox>
              </FormField>
              <FormField
                label={T.translate('TAG')}
                labelVariant={FormField.LABEL_VARIANTS.TOP}
              >
                <StatusSelectBox
                  style={{ maxHeight: 80, overflowY: 'auto' }}
                  multiple
                  searchable
                  clearable
                  showValueAsTags
                  options={tagOptions}
                  value={selectedTags}
                  isLoading={tagsLoading}
                  loadError={tagsError}
                  loadErrorContent={T.translate('LOAD_FILTER_ERROR')}
                  onRetry={retryTags}
                  placeholder={T.translate('SELECT')}
                  optionRenderer={optionRenderer}
                  onChange={(items) =>
                    setPanelTags((items || []).map((option) => option.value))
                  }
                  valueTagRenderer={(item) => valueTagRenderer(item)}
                >
                  {(props) => <VirtualizedSelectBox {...props} />}
                </StatusSelectBox>
              </FormField>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '8px',
                }}
              >
                <Checkbox
                  checked={panelWithAllTags}
                  onChange={(checked) => setPanelWithAllTags(checked)}
                />
                <label style={{ marginLeft: '12px' }}>
                  {T.translate('INCLUDE_ALL_TAGS')}
                </label>
              </div>
            </div>
          </>
        )}
      </Panel.Body>
    </Panel>
  );
};

SheetSearchPanel.propTypes = {
  /**
   * current filters object
   */
  filters: PropTypes.shape({
    searchExpression: PropTypes.string,
    setId: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
  }),
  /**
   * callback for update filters
   */
  setFilters: PropTypes.func,
  /**
   * whether the panel is open
   */
  open: PropTypes.bool,
};

export default SheetSearchPanel;
